import { useMutation, useQuery } from "@apollo/client";
import { gql } from "generated-graphql";
import { FacilityInput, FacilityStateFieldsQuery, Issue } from "generated-graphql/graphql";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { filterIssues, regulatoryFields } from "../Chemicals/Report/useReport";

export const FACILITY_STATE_FIELDS = gql(`
  query FacilityStateFields($input: FacilityStateFieldsInput!) {
    facilityStateFields(input: $input) {
      ...dynamicField
    }
  }
`);

gql(`
  fragment facilityRcraProfile on FacilityRcraProfile {
    id
    facilityId

    rcraSiteName

    rcraStreetAddress1
    rcraStreetAddress2
    rcraCity
    rcraState
    rcraZipCode
    rcraCountry
    rcraCounty
    rcraLatitude
    rcraLongitude
    useLatLongAsPrimary

    rcraMailingStreetAddress1
    rcraMailingStreetAddress2
    rcraMailingCity
    rcraMailingState
    rcraMailingZipCode
    rcraMailingCountry

    landType

    rcraNaicsCode
    otherNaicsCodes

    shortTermGenerator
    shortTermGeneratorNotes
    tsdActivity
    offSiteReceipt
    recyclerActivity
    exemptBoilerActivities

    transporterActivity
    undergroundInjectionControl
    usImporterOfHazardousWaste
    recognizedTraderActivity
    slabImporter

    lqhuwActivities
    universalWasteDestFacility

    usedOilTransporterActivities
    usedOilProcessorActivities
    offSpecUsedOilBurner
    usedOilMarketerActivities

    subpartPActivity
    subpartPWithdrawal

    subpartKEntityTypes
    subpartKWithdrawal

    manifestBroker

    isLqgConsolidator
  }
`);

gql(`
  fragment facilityProfile on Facility {
    id
    name
    businessUnit
    customerFacilityId
    companyName
    department
    phone
    phone24Hour
    maxNumOccupants
    isUnmanned
    facilityType
    collectionMode
    dbNumber
    naicsCode
    status
    statusReason
    dateOpened
    dateClosed
    dateInactive
    archivedAt
    cupaAnniversaryDate
    isSubjectToChemicalAccidentPrevention
    isSubjectToEmergencyPlanning
    isSubjectToToxicsReleaseInventoryReporting
    colocationReportingFacility {
      ...FacilityPicker
    }
    county
    municipality
    facilityAlternateIds {
      id
      type
      value
      hasNoId
      expiresAt
    }
    fireDepartmentName
    fireDepartment {
      ...FireDepartmentPicker
    }
    supplementalFireDepartments {
      id
      fireDepartment {
        ...FireDepartmentPicker
      }
    }
    lepcName
    lepc {
      ...LepcPicker
    }
    supplementalLepcs {
      id
      lepc {
        ...LepcPicker
      }
    }
    streetAddress1
    streetAddress2
    city
    zip
    state
    country

    mailingStreetAddress1
    mailingStreetAddress2
    mailingCity
    mailingState
    mailingZip
    mailingCountry
    mailingCompanyName

    isMailingAddressEncamp
    isMailingAddressSameAsFacility
    latitude
    longitude
    useEncampBillingContact
    wasteFeatureFlagOverride
    chemicalFeatureFlagOverride
    rcraProfileEnabled
    stateFields {
      ...dynamicField
    }
    renderPhone {
      inputMask
    }
    renderPhone24Hour {
      inputMask
    }
    issues {
      message
      key
      modelId
      modelName
      jurisdictions
      level
      metadata
    }
    tags {
      id
      name
    }
    isEpaIdLocked
    isOwnerOperatorLocked
    # facilityRcraProfile is intentionally excluded; ~40 callers of useFacility
    # don't need it. Use useFacilityRcraProfile() below instead.
  }
`);

export const GET_FACILITY = gql(`
  query GetFacility($id: ID!) {
    facility(id: $id) {
      ...facilityProfile
    }
  }`);

const UPDATE_FACILITY_PROFILE = gql(`
  mutation UpdateFacilityProfile($id: ID!, $facility: FacilityInput!) {
    updateFacility(id: $id, facility: $facility) {
      ...facilityProfile
    }
  }`);

export function useUpdateFacilityProfile() {
  return useMutation(UPDATE_FACILITY_PROFILE, {
    // GET_FACILITY refetch refreshes the facility's issue list, which drives the
    // waste submission stepper's per-step issue counts and status pills (via
    // useFacility → useSubmissionIssuesGroupedByStep). Without it, saving a step
    // left the overview/nav showing stale issues until a manual page reload.
    // `awaitRefetchQueries` keeps the Save action's loading state up until the
    // recomputed issues are in, so the UI never flashes stale.
    refetchQueries: ["FacilityStateFields", GET_FACILITY],
    awaitRefetchQueries: true,
  });
}

const GET_FACILITY_RCRA_PROFILE = gql(`
  query GetFacilityRcraProfile($id: ID!) {
    facility(id: $id) {
      id
      epaIdStatuses {
        epaId
        isInactive
        isPrimaryForFacility
      }
      facilityRcraProfile {
        ...facilityRcraProfile
      }
    }
  }
`);

/**
 * Fetches the RCRA Facility Profile for a facility. Prefer this over reading
 * `facilityRcraProfile` from `useFacility()` so unrelated callers don't pay
 * the fetch cost.
 *
 * @param id Facility ID; defaults to the `:facilityId` path parameter.
 */
export function useFacilityRcraProfile(id?: string) {
  const { facilityId: pathParamFacilityId } = useParams<{
    facilityId: string;
  }>();
  const facilityId = id ?? pathParamFacilityId ?? "";

  return useQuery(GET_FACILITY_RCRA_PROFILE, {
    variables: { id: facilityId },
    skip: !facilityId,
  });
}

const UPSERT_FACILITY_RCRA_PROFILE = gql(`
  mutation UpsertFacilityRcraProfile(
    $facilityId: ID!
    $input: FacilityRcraProfileInput!
  ) {
    upsertFacilityRcraProfile(facilityId: $facilityId, input: $input) {
      ...facilityRcraProfile
    }
  }
`);

export function useUpsertFacilityRcraProfile() {
  return useMutation(UPSERT_FACILITY_RCRA_PROFILE, {
    // GET_FACILITY refetch refreshes the issue list (and the "needs attention"
    // chips it drives) so RCRA fields stop showing as missing once saved.
    refetchQueries: [GET_FACILITY_RCRA_PROFILE, GET_FACILITY],
  });
}

export const GET_FACILITY_CONTACTS_FOR_PROFILE_V2 = gql(`
  query GetFacilityContactsForProfileV2($id: ID!, $programArea: ProgramArea) {
    facility(id: $id) {
      id
      state
      isOwnerOperatorLocked
      ...facilityContacts
      issues {
        ...issue
      }
    }
  }
`);

/**
 * Fetches contacts + state + issues for the Facility Profile V2 "Facility
 * Contacts" section. Mirrors `useFacilityRcraProfile`: kept off `useFacility()`
 * so unrelated callers don't pay the fetch cost. Issues are included so contact
 * mutations refresh both the inline grid and the section button via Apollo's
 * normalized `Facility` cache entry.
 *
 * `programArea` is declared (the `facilityContacts` fragment references it as
 * `contacts(programArea: $programArea)`) and intentionally left unset, so the
 * server returns contacts across all program areas — matching the current
 * V2 omnisearch grid behavior (no program-area filter).
 *
 * `fetchPolicy` is `"cache-and-network"` (unlike `useFacilityRcraProfile`)
 * because contacts are mutated frequently and a stale first-render is jarring.
 *
 * @param id Facility ID; defaults to the `:facilityId` path parameter.
 */
export function useFacilityContacts(id?: string) {
  const { facilityId: pathParamFacilityId } = useParams<{
    facilityId: string;
  }>();
  const facilityId = id ?? pathParamFacilityId ?? "";

  return useQuery(GET_FACILITY_CONTACTS_FOR_PROFILE_V2, {
    variables: { id: facilityId },
    skip: !facilityId,
    fetchPolicy: "cache-and-network",
  });
}

/**
 *
 * @param id ID of facility to fetch.
 * If not provided, the ID of the `:facilityId` path parameter will be used instead, if it is present.
 */
export function useFacility(id?: string) {
  const { facilityId: pathParamFacilityId } = useParams<{
    facilityId: string;
  }>();
  const facilityId = id ?? pathParamFacilityId ?? "";

  return useQuery(GET_FACILITY, {
    variables: { id: facilityId },
    skip: !facilityId,
  });
}

export function useFacilityStateFields(
  facilityId?: string,
  jurisdictionOverrides?: string[],
  options?: { onCompleted?: (data: FacilityStateFieldsQuery) => void }
) {
  return useQuery(FACILITY_STATE_FIELDS, {
    skip: !facilityId,
    variables: {
      input: {
        facilityId: facilityId ?? "",
        jurisdictions: jurisdictionOverrides ?? undefined,
      },
    },
    onCompleted: options?.onCompleted,
  });
}

/**
 * @param filterFn Optional predicate to filter the facility's issues.
 * @param facilityId Facility ID; defaults to the `:facilityId` path parameter.
 */
export function useFacilityIssues(
  filterFn?: (issue: Issue) => boolean,
  facilityId?: string
): Issue[] {
  const { data } = useFacility(facilityId);
  return useMemo(() => filterIssues(data?.facility?.issues ?? [], filterFn), [data, filterFn]);
}

export function useRegulatoryInfoIssues() {
  return useFacilityIssues(
    (issue) =>
      issue.modelName === "Facility" && regulatoryFields.some((re) => issue.key?.startsWith(re))
  );
}

export function useFacilityProfileIssues({ omitStateFields }: { omitStateFields?: boolean } = {}) {
  return useFacilityIssues(
    (issue) =>
      (issue.modelName === "Facility" ||
        issue.modelName === "FacilityStateField" ||
        issue.modelName === "FacilityAlternateId") &&
      issue.key !== "contacts" &&
      issue.key !== "facilityDocuments" &&
      issue.key !== "undergroundStorageTanks" &&
      // Exclude report Regulatory Info fields
      !regulatoryFields.some((re) => issue.key?.startsWith(re)) &&
      (omitStateFields ? issue.modelName !== "FacilityStateField" : true)
  );
}

export function useFacilityProfileIssuesV2({
  omitStateFields,
}: { omitStateFields?: boolean } = {}) {
  return useFacilityIssues((issue) => isFacilityProfileIssueV2(issue, { omitStateFields }));
}

type FacilityFieldKey = keyof FacilityInput;

/**
 * Type-safe helper to create a Set of facility field keys.
 * The generic constraint ensures only valid FacilityInput keys are accepted
 * at compile time, while the returned Set<string> allows easy .has() lookups
 * with issue.key (which is string | null).
 */
function facilityFieldSet<K extends FacilityFieldKey>(keys: readonly K[]): ReadonlySet<string> {
  return new Set<string>(keys);
}

/** Keys for fields editable in the Edit Basics dialog */
export const basicInformationKeys = facilityFieldSet([
  // Basic info
  "name",
  "customerFacilityId",
  "companyName",
  "department",
  "businessUnit",
  "phone",
  "phone24Hour",
  "dbNumber",
  "naicsCode",
  "tags",
  // Operational status
  "facilityType",
  "status",
  "statusReason",
  "dateOpened",
  "dateClosed",
  "dateInactive",
  "maxNumOccupants",
  "isUnmanned",
  "isColocated",
  "colocationReportingFacilityId",
  // Billing
  "useEncampBillingContact",
  // Government IDs
  "facilityAlternateIds",
] as const);

export function useBasicInformationIssues() {
  return useFacilityIssues(
    (issue) =>
      (issue.modelName === "Facility" &&
        issue.key != null &&
        basicInformationKeys.has(issue.key)) ||
      issue.modelName === "FacilityAlternateId"
  );
}

export const physicalAddressKeys = facilityFieldSet([
  "streetAddress1",
  "streetAddress2",
  "city",
  "state",
  "zip",
  "county",
  "municipality",
  "country",
  "latitude",
  "longitude",
] as const);

export const mailingAddressKeys = facilityFieldSet([
  "isMailingAddressSameAsFacility",
  "mailingStreetAddress1",
  "mailingStreetAddress2",
  "mailingCity",
  "mailingState",
  "mailingZip",
  "mailingCountry",
  "mailingCompanyName",
] as const);

export const addressKeys = new Set([...physicalAddressKeys, ...mailingAddressKeys]);

export function isAddressIssue(issue: Issue): boolean {
  return issue.modelName === "Facility" && issue.key != null && addressKeys.has(issue.key);
}

export function useAddressIssues() {
  return useFacilityIssues((issue) => isAddressIssue(issue));
}

export const localAgencyKeys = new Set([
  "lepcId",
  "fireDepartmentId",
  "supplementalLepcIds",
  "supplementalFireDepartmentIds",
]);

export function isLocalAgencyIssue(issue: Issue): boolean {
  return (
    (issue.modelName === "Facility" && issue.key != null && localAgencyKeys.has(issue.key)) ||
    issue.modelName === "Lepc" ||
    issue.modelName === "FireDepartment"
  );
}

export function useLocalAgencyIssues() {
  return useFacilityIssues((issue) => isLocalAgencyIssue(issue));
}

export function usePhysicalAddressIssues() {
  return useFacilityIssues(
    (issue) =>
      issue.modelName === "Facility" && issue.key != null && physicalAddressKeys.has(issue.key)
  );
}

export function useMailingAddressIssues() {
  return useFacilityIssues(
    (issue) =>
      issue.modelName === "Facility" && issue.key != null && mailingAddressKeys.has(issue.key)
  );
}

/**
 * @param facilityId Facility ID; defaults to the `:facilityId` path parameter.
 */
export function useStateInformationIssues(facilityId?: string) {
  return useFacilityIssues((issue) => issue.modelName === "FacilityStateField", facilityId);
}

export function isFacilityDocumentIssue(issue: Issue): boolean {
  return (
    (issue.key === "facilityDocuments" && issue.modelName === "Facility") ||
    issue.modelName === "FacilityDocument"
  );
}

export function useFacilityDocumentIssues() {
  return useFacilityIssues((issue) => isFacilityDocumentIssue(issue));
}

export function isCredentialIssue(issue: Issue): boolean {
  return issue.modelName === "Credential" || issue.modelName === "FacilityCredential";
}

export function useCredentialIssues() {
  return useFacilityIssues((issue) => isCredentialIssue(issue));
}

export function isContactIssue(issue: Issue): boolean {
  return (
    issue.key === "contacts" ||
    issue.modelName === "FacilityContact" ||
    issue.modelName === "Person"
  );
}

export function isStateInformationIssue(
  issue: Issue,
  { omitStateFields }: { omitStateFields?: boolean } = {}
): boolean {
  return issue.modelName === "FacilityStateField" && !omitStateFields;
}

export function isFacilityProfileIssueV2(
  issue: Issue,
  { omitStateFields }: { omitStateFields?: boolean } = {}
): boolean {
  if (issue.key === "undergroundStorageTanks") {
    return false;
  }

  if (regulatoryFields.some((re) => issue.key?.startsWith(re))) {
    return false;
  }

  if (isStateInformationIssue(issue, { omitStateFields })) {
    return true;
  }

  return (
    issue.modelName === "Facility" ||
    issue.modelName === "FacilityAlternateId" ||
    isContactIssue(issue) ||
    isFacilityDocumentIssue(issue) ||
    isCredentialIssue(issue) ||
    isLocalAgencyIssue(issue)
  );
}

export function useContactIssues() {
  return useFacilityIssues((issue) => isContactIssue(issue));
}

export function useFacilityContactIssues() {
  return useFacilityIssues((issue) => issue.key === "contacts");
}

export function useFacilityContactModelIssues() {
  return useFacilityIssues(
    (issue) => issue.modelName === "FacilityContact" || issue.modelName === "Person"
  );
}

/**
 * @param facilityId Facility ID; defaults to the `:facilityId` path parameter.
 */
export function useRcraProfileIssues(facilityId?: string) {
  return useFacilityIssues(
    (issue) =>
      issue.modelName === "FacilityRcraProfile" ||
      (issue.modelName === "Facility" && issue.key === "facilityRcraProfile"),
    facilityId
  );
}
